import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Target,
  Search,
  ClipboardCheck,
  UserCheck,
  Calendar,
  Handshake,
  CheckCircle,
  Building2,
  Factory,
  Cpu,
  Radio,
  Tractor,
  Cog,
  Briefcase,
  TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';

export const Placement = () => {
  const navigate = useNavigate();

  const recruitmentProcess = [
    {
      icon: Users,
      title: 'Client Consultation',
      description: 'Initial conversation between a recruiter and the hiring manager/company to understand their hiring needs, expectations, and success criteria. It sets the foundation for the entire recruitment process.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      icon: Search,
      title: 'Talent Sourcing',
      description: 'We utilize the proactive process of finding, identifying, and engaging potential candidates before they apply. It focuses on building a pipeline of qualified talent, especially for hard-to-fill or high-volume roles.',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      icon: ClipboardCheck,
      title: 'Screening & Assessment',
      description: 'Conduct Initial evaluates whether a candidate meets the required qualifications—skills, experience, cultural fit, and potential—to move forward in the hiring process. It ensures only the most qualified candidates reach the interview stage.',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      icon: UserCheck,
      title: 'Candidate Presentation',
      description: 'Shortlisted candidates are presented to clients with detailed profiles and assessment reports, providing all necessary information for informed decision-making.',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      icon: Calendar,
      title: 'Interview Coordination',
      description: 'Scheduling, organizing, and managing interviews between candidates and hiring teams to ensure a smooth, efficient, and professional experience for everyone involved.',
      color: 'text-pink-600',
      bgColor: 'bg-pink-50'
    },
    {
      icon: Handshake,
      title: 'Offer and Onboarding',
      description: 'This phase includes preparing, extending, negotiating, and securing acceptance of the job offer between candidate and client.',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50'
    }
  ];

  const industries = [
    { icon: Cpu, name: 'IT/ITES Sector', color: 'text-blue-600' },
    { icon: Radio, name: 'Telecommunication Sector', color: 'text-purple-600' },
    { icon: Factory, name: 'Manufacturing Industries', color: 'text-orange-600' },
    { icon: Building2, name: 'Infrastructure Sector', color: 'text-emerald-600' },
    { icon: Cog, name: 'Automotive Industries', color: 'text-red-600' },
    { icon: Tractor, name: 'Agriculture Industries', color: 'text-green-600' },
    { icon: Cog, name: 'Engineering Industries', color: 'text-gray-600' }
  ];

  const benefits = [
    'Expertise, extensive network, and tailored approach',
    'Right fit for role and company culture',
    'Complete recruitment process management',
    'Time-saving and risk reduction',
    'Focus on professionalism and transparency',
    'Long-term partnerships',
    'Attract, hire, and retain the best talent'
  ];

  return (
    <>
      <Helmet>
        <title>Placement Services - ISDE Academy India Pvt Ltd | Recruitment & Training</title>
        <meta
          name="description"
          content="ISDE Academy provides comprehensive placement and recruitment services, bridging the gap between college and industries with high-tech training and HR solutions."
        />
        <meta
          name="keywords"
          content="placement services, recruitment, hiring, HR solutions, talent sourcing, ISDE Academy, job placement, campus recruitment"
        />
        <meta property="og:title" content="Placement Services - ISDE Academy India Pvt Ltd" />
        <meta property="og:description" content="Pioneer in recruiting candidates across various industrial verticals including Automotive, IT, Engineering, and more." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://isde.academy/placement" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-emerald-600 via-blue-600 to-indigo-700 text-white py-24 mt-20 overflow-hidden">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob"></div>
            <div className="absolute top-0 right-1/4 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-4000"></div>
          </div>

          {/* Decorative Icons */}
          <div className="absolute top-20 right-20 opacity-10">
            <Briefcase className="h-40 w-40" />
          </div>
          <div className="absolute bottom-20 left-20 opacity-10">
            <Users className="h-32 w-32" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center md:text-left"
              >
                <motion.div
                  className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Handshake className="h-5 w-5 text-emerald-200" />
                  <span className="text-sm font-semibold text-emerald-100">Professional Recruitment Services</span>
                </motion.div>

                <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                  Placement Services
                </h1>
                <p className="text-2xl md:text-3xl text-blue-100 font-semibold mb-4">
                  Bridging the Gap Between College and Industries
                </p>
                <p className="text-lg text-blue-100 mb-8 leading-relaxed max-w-2xl">
                  Pioneer in recruiting candidates across various industrial verticals with expert HR professionals and comprehensive training solutions.
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <Button
                    onClick={() => navigate('/contact')}
                    className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all"
                  >
                    Get in Touch
                  </Button>
                  <Button
                    onClick={() => navigate('/training-programs')}
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold px-8 py-6 text-lg"
                  >
                    View Training
                  </Button>
                </div>
              </motion.div>

              {/* Right Content - Feature Cards */}
              <motion.div
                className="grid grid-cols-2 gap-4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <motion.div
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
                  transition={{ duration: 0.2 }}
                >
                  <Briefcase className="h-10 w-10 mb-3 text-emerald-200" />
                  <div className="text-3xl font-bold mb-2">Multi-Sector</div>
                  <div className="text-blue-100 text-sm">Industry Coverage</div>
                </motion.div>

                <motion.div
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
                  transition={{ duration: 0.2 }}
                >
                  <Target className="h-10 w-10 mb-3 text-blue-200" />
                  <div className="text-3xl font-bold mb-2">6 Step</div>
                  <div className="text-blue-100 text-sm">Recruitment Process</div>
                </motion.div>

                <motion.div
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
                  transition={{ duration: 0.2 }}
                >
                  <Users className="h-10 w-10 mb-3 text-purple-200" />
                  <div className="text-3xl font-bold mb-2">Expert</div>
                  <div className="text-blue-100 text-sm">HR Professionals</div>
                </motion.div>

                <motion.div
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.15)" }}
                  transition={{ duration: 0.2 }}
                >
                  <TrendingUp className="h-10 w-10 mb-3 text-indigo-200" />
                  <div className="text-3xl font-bold mb-2">Hi-Tech</div>
                  <div className="text-blue-100 text-sm">Training Programs</div>
                </motion.div>
              </motion.div>
            </div>
          </div>

          {/* Bottom Wave */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="rgb(249, 250, 251)"/>
            </svg>
          </div>
        </section>

        {/* About Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-800 mb-6">
                  About ISDE Academy India Pvt Ltd
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  ISDE Academy India P Ltd has started its Recruitment services and also bridging the Gap between College and Industries by providing Hitech training to students for Placement. We have a strong team of HR professionals who can cater to various clients with our core specialization in providing any kind of HR services for any type of industry.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  We are <span className="font-semibold text-blue-600">Pioneer in Recruiting</span> candidates in Various Industrials Verticals like Automotive industries, Agricultural industries, Engineering Industries, Electronics, IT, ITES and Service Industries.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Recruitment Process Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                  Recruitment Process
                </h2>
                <p className="text-lg text-gray-600">
                  Our systematic approach to finding the perfect talent for your organization
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recruitmentProcess.map((step, index) => {
                  const IconComponent = step.icon;
                  return (
                    <div
                      key={index}
                      className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 hover:border-blue-200"
                    >
                      <div className={`${step.bgColor} w-16 h-16 rounded-lg flex items-center justify-center mb-4`}>
                        <IconComponent className={`h-8 w-8 ${step.color}`} />
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-3">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-lg p-8 md:p-12 border border-blue-100">
                <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                  Benefits of Choosing Us
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-8">
                  Our expertise, extensive network, and tailored approach ensure that every candidate we present is the right fit for your role and company culture. From sourcing and screening to offer management and onboarding, we handle the entire recruitment process, saving you time and reducing hiring risks. With a focus on professionalism, transparency, and long-term partnerships, we help your organization attract, hire, and retain the best talent to drive growth and success.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Industries Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                  Industries We Serve
                </h2>
                <p className="text-lg text-gray-600">
                  Offering recruitment services across diverse sectors
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {industries.map((industry, index) => {
                  const IconComponent = industry.icon;
                  return (
                    <div
                      key={index}
                      className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 p-6 text-center border border-gray-100 hover:border-blue-200 group"
                    >
                      <div className="bg-gray-50 group-hover:bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-colors">
                        <IconComponent className={`h-8 w-8 ${industry.color}`} />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {industry.name}
                      </h3>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl shadow-2xl p-8 md:p-12 text-center text-white">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Ready to Find the Perfect Talent?
                </h2>
                <p className="text-xl text-blue-100 mb-8">
                  Let our expert team help you build a world-class workforce
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/contact"
                    className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-8 py-3 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <Users className="h-5 w-5" />
                    Contact Our HR Team
                  </a>
                  <a
                    href="tel:04449591335"
                    className="bg-blue-500 hover:bg-blue-400 text-white font-semibold px-8 py-3 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
                  >
                    Call: 044 49591335
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
